import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

/**
 * Chiara — AI Sales Assistant chatbot.
 *
 * Floating bubble (bottom-right) that opens a chat panel. Persists session
 * + history in sessionStorage so the conversation survives page navigation
 * inside the same browser tab.
 *
 * Backend: POST /api/chat/message → {reply, products, coupon}
 *
 * Hidden on /gestione (admin pages) and on the checkout flow to avoid
 * distracting paying customers.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const STORAGE_SESSION = "cera_chat_session";
const STORAGE_HISTORY = "cera_chat_history";
const STORAGE_OPEN = "cera_chat_was_open";
const STORAGE_DISMISSED = "cera_chat_dismissed_at";

const newSessionId = () =>
  `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const INTRO_MESSAGE = {
  role: "assistant",
  content:
    "👋 Ciao! Sono Chiara, l'assistente di Edicola Cerasuolo. Cosa stai cercando? Modellismo, Pokémon, carte da collezione? Dimmi pure!",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INTRO_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [products, setProducts] = useState([]);
  const [sessionId] = useState(() => {
    try {
      let s = sessionStorage.getItem(STORAGE_SESSION);
      if (!s) {
        s = newSessionId();
        sessionStorage.setItem(STORAGE_SESSION, s);
      }
      return s;
    } catch {
      return newSessionId();
    }
  });
  const bodyRef = useRef(null);

  // Hide on admin / login / checkout
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const hideOnPaths = ["/gestione", "/checkout", "/admin", "/login"];
  const shouldHide = hideOnPaths.some((p) => path.startsWith(p));

  // Restore history + open state
  useEffect(() => {
    try {
      const h = sessionStorage.getItem(STORAGE_HISTORY);
      if (h) {
        const parsed = JSON.parse(h);
        if (Array.isArray(parsed) && parsed.length) setMessages(parsed);
      }
      const wasOpen = sessionStorage.getItem(STORAGE_OPEN) === "1";
      if (wasOpen) setOpen(true);
    } catch { /* ignore */ }
  }, []);

  // Persist history
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_HISTORY, JSON.stringify(messages)); } catch { /* ignore */ }
  }, [messages]);

  // Persist open state
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_OPEN, open ? "1" : "0"); } catch { /* ignore */ }
  }, [open]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, products, coupon, loading]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg = { role: "user", content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      // Pass last 20 turns of history (excluding the intro placeholder)
      const cleanHistory = next.slice(-20).filter((m) => m !== INTRO_MESSAGE);
      // Read logged-in customer email if available (for coupon targeting)
      let customerEmail = null;
      try {
        const u = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "null");
        customerEmail = u?.email || null;
      } catch { /* ignore */ }

      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
        history: cleanHistory,
        customer_email: customerEmail,
      });
      const data = res.data || {};
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "..." }]);
      setProducts(Array.isArray(data.products) ? data.products : []);
      if (data.coupon && data.coupon.code) {
        setCoupon(data.coupon);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Connessione non riuscita, riprova fra un attimo.";
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${detail}` }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, sessionId]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    if (!window.confirm("Vuoi davvero ricominciare la conversazione?")) return;
    setMessages([INTRO_MESSAGE]);
    setProducts([]);
    setCoupon(null);
    try {
      sessionStorage.removeItem(STORAGE_HISTORY);
    } catch { /* ignore */ }
  };

  const copyCoupon = async () => {
    if (!coupon?.code) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
    } catch { /* ignore */ }
  };

  const dismissBubble = (e) => {
    e?.stopPropagation();
    try { localStorage.setItem(STORAGE_DISMISSED, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };

  // Dismissed for 1h? Hide the closed bubble (still re-openable manually with the floating icon)
  const recentlyDismissed = (() => {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_DISMISSED) || "0", 10);
      return Date.now() - v < 60 * 60 * 1000;
    } catch { return false; }
  })();

  if (shouldHide) return null;

  return (
    <>
      {/* Floating launcher button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Apri chat di assistenza"
          data-testid="chat-launcher-btn"
          className="fixed bottom-20 right-4 z-[70] w-14 h-14 rounded-full bg-amber-500 text-white shadow-lg hover:bg-amber-400 transition-all flex items-center justify-center text-2xl border-2 border-white md:bottom-6"
          style={{ boxShadow: "0 10px 25px -3px rgba(245, 158, 11, 0.5)" }}
        >
          💬
          {!recentlyDismissed && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-[70] bg-white shadow-2xl border border-gray-200 flex flex-col
                     bottom-0 right-0 left-0 h-[80vh] rounded-t-2xl
                     md:bottom-4 md:right-4 md:left-auto md:h-[600px] md:w-[400px] md:rounded-2xl"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white text-amber-600 flex items-center justify-center font-bold">
                C
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">Chiara</p>
                <p className="text-[11px] opacity-90 leading-tight">Assistente AI · risponde subito</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={reset}
                className="text-white/80 hover:text-white text-xs px-2 py-1"
                title="Ricomincia"
                data-testid="chat-reset-btn"
              >
                ↻
              </button>
              <button
                type="button"
                onClick={dismissBubble}
                className="text-white/80 hover:text-white text-xl px-2"
                title="Chiudi"
                data-testid="chat-close-btn"
              >
                ×
              </button>
            </div>
          </div>

          {/* Coupon banner */}
          {coupon?.code && (
            <div
              className="mx-3 mt-3 p-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white"
              data-testid="chat-coupon-banner"
            >
              <p className="text-xs font-semibold">🎁 Coupon -{coupon.discount_percent}% attivato!</p>
              <div className="flex items-center justify-between mt-1 gap-2">
                <code
                  className="bg-white text-emerald-700 font-mono font-bold px-2 py-1 rounded text-sm flex-1 text-center"
                  data-testid="chat-coupon-code"
                >
                  {coupon.code}
                </code>
                <button
                  type="button"
                  onClick={copyCoupon}
                  className="bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-2 py-1 rounded"
                  data-testid="chat-coupon-copy-btn"
                >
                  Copia
                </button>
              </div>
              <p className="text-[10px] opacity-90 mt-1">Valido 7 giorni. Inseriscilo al checkout.</p>
            </div>
          )}

          {/* Messages */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${idx}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-amber-500 text-white rounded-br-sm"
                      : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {/* Suggested products */}
            {products.length > 0 && (
              <div className="space-y-2" data-testid="chat-products">
                {products.map((p) => (
                  <a
                    key={p.id}
                    href={p.url}
                    className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-2 hover:border-amber-400 transition-colors"
                    data-testid={`chat-product-${p.id}`}
                  >
                    {p.image && (
                      <img
                        src={p.image}
                        alt=""
                        className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                        loading="lazy"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 line-clamp-2 leading-tight">
                        {p.title}
                      </p>
                      <p className="text-sm font-bold text-amber-600 mt-0.5">€ {p.price.toFixed(2)}</p>
                      {p.availability === "preorder" && (
                        <span className="text-[10px] text-purple-600 font-semibold">PREVENDITA</span>
                      )}
                      {p.availability === "out_of_stock" && (
                        <span className="text-[10px] text-red-500 font-semibold">ESAURITO</span>
                      )}
                    </div>
                    <span className="text-amber-500 text-xl flex-shrink-0">›</span>
                  </a>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start" data-testid="chat-loading">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-500">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrivi un messaggio…"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:border-amber-400"
                disabled={loading}
                data-testid="chat-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={!input.trim() || loading}
                className="bg-amber-500 hover:bg-amber-400 text-white px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-40"
                data-testid="chat-send-btn"
              >
                ➤
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 text-center">
              Chiara è un assistente AI · risposte indicative, conferma sempre al checkout
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
